<!-- components/forms/ContentTypeTabs.vue -->
<script setup>
import { computed } from 'vue'
import { 
  DocumentTextIcon,
  LinkIcon,
  UserIcon,
  WifiIcon,
  ChatBubbleLeftIcon,
  EnvelopeIcon,
  RectangleStackIcon,
  SparklesIcon
} from '@heroicons/vue/24/outline'

// Props & Emits
const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  showBatch: {
    type: Boolean,
    default: true
  },
  showStyled: {
    type: Boolean,
    default: true
  },
  batchCount: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['update:modelValue'])

// Tab configuration
const baseTabs = [
  {
    id: 'text',
    label: 'Text',
    icon: DocumentTextIcon,
    description: 'Plain text content',
    category: 'basic'
  },
  {
    id: 'url',
    label: 'URL',
    icon: LinkIcon,
    description: 'Website links',
    category: 'basic'
  },
  {
    id: 'vcard',
    label: 'Contact',
    icon: UserIcon,
    description: 'Contact card (vCard)',
    category: 'advanced'
  },
  {
    id: 'wifi',
    label: 'WiFi',
    icon: WifiIcon,
    description: 'WiFi network credentials',
    category: 'advanced'
  },
  // {
  //   id: 'sms',
  //   label: 'SMS',
  //   icon: ChatBubbleLeftIcon,
  //   description: 'Text message',
  //   category: 'basic'
  // },
  // {
  //   id: 'email',
  //   label: 'Email',
  //   icon: EnvelopeIcon,
  //   description: 'Email address',
  //   category: 'basic'
  // }
]

const advancedTabs = [
  // {
  //   id: 'batch',
  //   label: 'Batch',
  //   icon: RectangleStackIcon,
  //   description: 'Multiple QR codes',
  //   category: 'advanced',
  //   badge: props.batchCount > 0 ? props.batchCount : null,
  //   show: props.showBatch
  // },
  // {
  //   id: 'styled',
  //   label: 'Styled',
  //   icon: SparklesIcon,
  //   description: 'Custom designs',
  //   category: 'premium',
  //   show: props.showStyled
  // }
]

// Computed
const tabs = computed(() => {
  const allTabs = [...baseTabs]
  
  // Add advanced tabs based on props
  advancedTabs.forEach(tab => {
    if (tab.show !== false) {
      allTabs.push({
        ...tab,
        badge: tab.id === 'batch' ? props.batchCount || null : tab.badge
      })
    }
  })
  
  return allTabs
})

const activeTab = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const tabsByCategory = computed(() => {
  return tabs.value.reduce((acc, tab) => {
    if (!acc[tab.category]) {
      acc[tab.category] = []
    }
    acc[tab.category].push(tab)
    return acc
  }, {})
})

// Methods
const selectTab = (tabId) => {
  activeTab.value = tabId
}

const isActive = (tabId) => {
  return activeTab.value === tabId
}

const getTabClasses = (tab) => {
  const isActiveTab = isActive(tab.id)
  const baseClasses = 'hover:bg-gray-50 hover:shadow-sm transform hover:scale-[1.02]'
  
  if (isActiveTab) {
    switch (tab.category) {
      case 'premium':
        return `bg-purple-50 text-purple-700 border-2 border-purple-300 shadow-md shadow-purple-100 ring-2 ring-purple-100`
      case 'advanced':
        return `bg-indigo-50 text-indigo-700 border-2 border-indigo-300 shadow-md shadow-indigo-100 ring-2 ring-indigo-100`
      default:
        return `bg-blue-50 text-blue-700 border-2 border-blue-300 shadow-md shadow-blue-100 ring-2 ring-blue-100`
    }
  }
  
  return `${baseClasses} text-gray-600 border-2 border-transparent hover:text-gray-900 hover:border-gray-200`
}

const getBadgeClasses = (tab) => {
  switch (tab.category) {
    case 'premium':
      return 'bg-purple-200 text-purple-800'
    case 'advanced':
      return 'bg-indigo-200 text-indigo-800'
    default:
      return 'bg-blue-200 text-blue-800'
  }
}
</script>

<template>
  <div class="bg-white border-b border-gray-200 px-4 py-3 min-w-0">
    <div class="max-w-7xl mx-auto min-w-0">
      <!-- Header with title and legend -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center space-x-2">
          <DocumentTextIcon class="w-5 h-5 text-blue-600" />
          <h3 class="text-lg font-medium text-gray-900">Content Type</h3>
        </div>
        
        <!-- Simple legend for desktop -->
        <div class="hidden md:flex items-center space-x-4 text-xs text-gray-500">
          <div class="flex items-center space-x-1">
            <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>Basic</span>
          </div>
          <div class="flex items-center space-x-1">
            <div class="w-2 h-2 bg-indigo-500 rounded-full"></div>
            <span>Advanced</span>
          </div>
          <div v-if="showStyled" class="flex items-center space-x-1">
            <div class="w-2 h-2 bg-purple-500 rounded-full"></div>
            <span>Premium</span>
          </div>
        </div>
      </div>

      <!-- Desktop horizontal tabs (scroll on tablet if needed) -->
      <div class="hidden sm:block min-w-0">
        <nav class="flex space-x-0.5 overflow-x-auto scrollbar-hide pb-1 min-w-0">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="selectTab(tab.id)"
            class="flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 min-h-[44px]"
            :class="getTabClasses(tab)"
          >
            <component :is="tab.icon" class="w-4 h-4 flex-shrink-0" />
            <span class="hidden md:inline">{{ tab.label }}</span>
            <span class="md:hidden text-xs">{{ tab.label }}</span>
            <span v-if="tab.badge" class="px-1.5 py-0.5 text-xs rounded-full flex-shrink-0" :class="getBadgeClasses(tab)">
              {{ tab.badge }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Mobile dropdown (usable at 320px, 44px touch target) -->
      <div class="sm:hidden w-full min-w-0">
        <select
          v-model="activeTab"
          class="w-full min-w-0 max-w-full px-3 py-3 min-h-[44px] border border-gray-300 rounded-lg bg-white text-sm"
        >
          <optgroup v-for="(categoryTabs, category) in tabsByCategory" :key="category" :label="category.charAt(0).toUpperCase() + category.slice(1)">
            <option v-for="tab in categoryTabs" :key="tab.id" :value="tab.id">
              {{ tab.label }}{{ tab.badge ? ` (${tab.badge})` : '' }}
            </option>
          </optgroup>
        </select>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Mobile select styling */
select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 40px;
}

/* Optgroup styling for mobile */
optgroup {
  font-weight: 600;
  color: #374151;
}

option {
  font-weight: 400;
  color: #6b7280;
}

/* Enhanced transitions */
button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

button:hover {
  transition-duration: 0.15s;
}

/* Focus styles for accessibility */
button:focus,
select:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Better spacing for medium screens */
@media (min-width: 768px) and (max-width: 1023px) {
  .space-x-0\.5 > :not([hidden]) ~ :not([hidden]) {
    margin-left: 0.25rem;
  }
}

/* Ensure proper flex behavior on smaller screens */
@media (max-width: 767px) {
  nav {
    padding-bottom: 0.5rem;
  }
}
</style>